// NodeThumb.jsx

import React, { useMemo, useCallback } from 'react';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// CSS
import './NodeThumb.css';

/**
 * NodeThumb
 * 
 * A small thumbnail component for displaying a node's image or video.
 * 
 * @param {object} node The node node.
 * @param {Function} onClick The click node handler.
 * @param {string} width The width of the thumbnail (default is "60px").
 * @param {string} height The height of the thumbnail (default is "60px").
 * @param {PhotoShape} shape Determines whether the thumb renders as a circle or square.
 * 
 * @returns {ReactNode} NodeThumb component.
 */
const NodeThumb = React.memo(({
    node,
    onClick,
    width = '60px',
    height = '60px',
    shape = PhotoShape.SQUARE,
}) => {

    // ----- MEMOS ----- //

    // Memoized URL calculation - check coverUrl first, then photo URL

    const backgroundUrl = useMemo(() => {
        // Check for coverUrl first
        if (node?.coverUrl) {
            return node.coverUrl;
        }

        if (node?.type === 'PHOTO' && typeof node?.url === 'string') {
            return node.url;
        }

        return null;
    }, [node?.coverUrl, node?.type, node?.url]);

    const borderRadius = useMemo(
        () => (shape === PhotoShape.CIRCLE ? '50%' : '7px'),
        [shape]
    );

    const containerStyle = useMemo(() => ({
        width,
        height,
        borderRadius,
        overflow: 'hidden',
    }), [width, height, borderRadius]);

    const mediaWrapperStyle = useMemo(() => ({
        width,
        height,
        borderRadius,
        overflow: 'hidden',
    }), [width, height, borderRadius]);

    // ----- HANDLERS ----- //

    // Memoized click handler

    const handleClick = useCallback((e) => {
        if (onClick) onClick(e, node);
    }, [onClick, node]);

    if (!backgroundUrl) return null;
    // ----- RENDER ----- //

    return (

        <div
          className="node-thumb-container"
          style={containerStyle}
          onClick={handleClick}
        >

            {/* WRAPPER */}
            <div
              className="node-thumb-image-wrapper"
              style={mediaWrapperStyle}
            >
                {/* IMAGE */}
                <img
                  className="node-thumb-image"
                  style={{
                    borderRadius,
                  }}
                  alt="Node"
                  src={backgroundUrl}
                />
            </div>

        </div>

    );
});

// Dev Tools
NodeThumb.displayName = 'NodeThumb';

export default NodeThumb;
